/**
 * chain-eos/permissions.ts
 *
 * Key / permission management — `updateauth`. Lets a user rotate the key(s)
 * on their own owner or active permission, and read the current permission
 * tree for display.
 *
 * Trap (see eos-backend-dev skill, trap #2): never hardcode 'active' as the
 * signing permission. Updating `owner` must be signed by `owner` itself
 * (nothing outranks it); updating `active` may be signed by `owner` or by
 * `active`'s own current key — the caller passes whichever permission the
 * live session actually holds.
 */

import { Name, PublicKey } from '@wharfkit/antelope'
import type { Session } from './session.js'
import { SYSTEM_CONTRACT } from './config.js'
import { getAccountInfo } from './session.js'

export interface AccountPermission {
  permission: string
  parent: string
  threshold: number
  keys: { key: string; weight: number }[]
  accounts: { actor: string; permission: string; weight: number }[]
}

/** Reads the live permission tree for an account (owner, active, and any custom permissions). */
export async function getAccountPermissions(accountName: string): Promise<AccountPermission[]> {
  const info = await getAccountInfo(accountName)
  const perms = (info as unknown as { permissions?: unknown[] }).permissions ?? []

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (perms as any[]).map(p => ({
    permission: String(p.perm_name),
    parent: String(p.parent),
    threshold: Number(p.required_auth?.threshold ?? 1),
    keys: (p.required_auth?.keys ?? []).map((k: { key: unknown; weight: unknown }) => ({
      key: String(k.key),
      weight: Number(k.weight),
    })),
    accounts: (p.required_auth?.accounts ?? []).map(
      (a: { permission: { actor: unknown; permission: unknown }; weight: unknown }) => ({
        actor: String(a.permission.actor),
        permission: String(a.permission.permission),
        weight: Number(a.weight),
      }),
    ),
  }))
}

export interface UpdateAuthParams {
  /** The account whose permission is being changed. */
  account: string
  /** 'owner' | 'active' | a custom permission name. */
  permission: 'owner' | 'active' | string
  /** Parent permission. Required for anything but 'owner' (which has none — pass ''). */
  parent?: string
  /** New single-key authority (threshold 1, this key only) for the permission. */
  newKey: string
  /**
   * Which permission on `account` signs this transaction. 'owner' when
   * rotating owner itself; otherwise the session's own live permission
   * (`session.permission.toString()`), never a literal
   * 'active'.
   */
  signingPermission: string
}

/**
 * Replaces a permission's authority with a single new key (threshold 1).
 * This REPLACES the whole authority — any co-signers or linked accounts on
 * that permission are dropped. Fine for the common "I lost/rotated my key"
 * case this screen targets; not a general-purpose multisig-authority editor.
 */
export async function updateAuthKey(session: Session, params: UpdateAuthParams): Promise<string> {
  const account = params.account.trim()
  const permission = params.permission.trim()
  const parent = params.parent ?? (permission === 'owner' ? '' : 'owner')
  const pub = PublicKey.from(params.newKey)

  const result = await session.transact({
    action: {
      account: SYSTEM_CONTRACT,
      name: 'updateauth',
      authorization: [{ actor: account, permission: params.signingPermission }],
      data: {
        account: Name.from(account),
        permission,
        parent,
        auth: {
          threshold: 1,
          keys: [{ key: pub, weight: 1 }],
          accounts: [],
          waits: [],
        },
      },
    },
  })

  return (result.response as { transaction_id?: string })?.transaction_id ?? ''
}
